import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

/*
 * Card logos as Banca Intesa requires (EPM v3.5 rules 2.2, bank feedback 2026-10-16, examples in
 * _Work/Intesa-eCommerce/3D Pay Hosting/logo/Card logotypes/primer korektnog brendiranja *.png):
 * - acceptance marks (Maestro, Mastercard, DinaCard, Visa, Amex) in one group;
 * - program marks (Mastercard ID Check, Visa Secure, Amex SafeKey, DinaCard Secure) in a separate
 *   group; ID Check and Visa Secure link to the card schemes' pages;
 * - every logo in a group has the same height (the bank asked to align by height);
 * - the Banca Intesa logo, unchanged and linked to bancaintesa.rs, sits between the groups;
 * - the strip is either one row or three stacked rows, never two: the layout is chosen from the
 *   measured width, and a group never wraps.
 * Files come from the bank's package, only with their empty margins trimmed so equal heights
 * look equal (visa.png keeps its official clear space).
 */

interface Logo {
  src: string;
  alt: string;
  /** width / height of the file, to size the row before the images load */
  ratio: number;
  href?: string;
}

const ACCEPTANCE: Logo[] = [
  { src: "maestro.png", alt: "Maestro", ratio: 175 / 136 },
  { src: "mastercard.png", alt: "Mastercard", ratio: 175 / 136 },
  { src: "dinacard.png", alt: "DinaCard", ratio: 150 / 70 },
  { src: "visa.png", alt: "Visa", ratio: 1106 / 531 },
  { src: "amex.png", alt: "American Express", ratio: 1 },
];

const PROGRAM: Logo[] = [
  {
    src: "mastercard-id-check.png",
    alt: "Mastercard ID Check",
    ratio: 235 / 160,
    href: "https://www.mastercard.rs/sr-rs/korisnici/pronadite-karticu.html",
  },
  {
    src: "visa-secure.png",
    alt: "Visa Secure",
    ratio: 185 / 160,
    href: "https://rs.visa.com/pay-with-visa/security-and-assistance/protected-everywhere.html",
  },
  { src: "amex-safekey.png", alt: "American Express SafeKey", ratio: 291 / 72 },
  { src: "dinacard-secure.png", alt: "DinaCard Secure", ratio: 430 / 160 },
];

const LINK_BANK = "https://www.bancaintesa.rs/";
const BANK_WIDTH = 170;

/* Logo height in a group: 36 px, smaller only when the group would not fit. At 36 px Amex SafeKey
   is 145 px wide, within the 155 px limit for program marks. */
const MAX_HEIGHT = 36;
/* One row only while the logos can stay at least this high; below it, three rows. */
const ROW_MIN_HEIGHT = 30;
/* Acceptance marks must be at least 23 px high. */
const MIN_HEIGHT = 23;
const GAP = 8;
const GROUP_GAP = 32;
const PADDING = 8;

const sumRatio = (logos: Logo[]) => logos.reduce((s, l) => s + l.ratio, 0);
const clampHeight = (h: number) => Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, Math.floor(h)));

/* Height that fits the given logos and fixed extras into the width. */
const fitHeight = (logos: Logo[], width: number, extra = 0) =>
  clampHeight((width - 2 * PADDING - (logos.length - 1) * GAP - extra) / sumRatio(logos));

const ALL = [...ACCEPTANCE, ...PROGRAM];
/* In one row the bank logo and two group gaps share the width; there is no gap across groups. */
const rowHeight = (width: number) => fitHeight(ALL, width, BANK_WIDTH + 2 * GROUP_GAP - GAP);

/* White panel so the light-background logos stay legible in dark mode. */
const Wrap = styled.div<{ $row: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.$row ? "row" : "column")};
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: ${(p) => (p.$row ? `${GROUP_GAP}px` : "14px")};
  padding: 12px ${PADDING}px;
  margin: 8px 0;
  background: #fff;
  border-radius: 8px;
`;

const Group = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: ${GAP}px;
`;

const Img = styled.img`
  display: block;
  width: auto;
`;

const base = "/assets/cards/";

/** Width of the element, kept up to date. 0 until measured. */
const useWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let frame = 0;
    // Re-rendering changes the strip's layout, which would resize it again inside the same
    // observer callback ("ResizeObserver loop completed with undelivered notifications").
    // Applying the width on the next frame, and only when it changed, avoids that.
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        setWidth((prev) => (prev === el.clientWidth ? prev : el.clientWidth))
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);
  return { ref, width };
};

const LogoGroup: React.FC<{ logos: Logo[]; height: number }> = ({ logos, height }) => (
  <Group>
    {logos.map((logo) => {
      const img = (
        <Img
          key={logo.src}
          src={base + logo.src}
          alt={logo.alt}
          title={logo.alt}
          style={{ height, minWidth: Math.round(logo.ratio * height) }}
        />
      );
      return logo.href ? (
        <a key={logo.src} href={logo.href} target="_blank" rel="noopener noreferrer">
          {img}
        </a>
      ) : (
        img
      );
    })}
  </Group>
);

export const PaymentBrands: React.FC = () => {
  const { ref, width } = useWidth();
  // Before the first measurement: three rows at full height, which fits any desktop width.
  const measured = width > 0 ? width : 1000;
  const inRow = rowHeight(measured);
  const row = width > 0 && inRow >= ROW_MIN_HEIGHT;

  return (
    <Wrap ref={ref} $row={row}>
      <LogoGroup logos={ACCEPTANCE} height={row ? inRow : fitHeight(ACCEPTANCE, measured)} />
      <a href={LINK_BANK} target="_blank" rel="noopener noreferrer">
        <Img
          src={base + "banca-intesa.png"}
          alt="Banca Intesa"
          title="Banca Intesa"
          style={{ width: BANK_WIDTH, height: "auto" }}
        />
      </a>
      <LogoGroup logos={PROGRAM} height={row ? inRow : fitHeight(PROGRAM, measured)} />
    </Wrap>
  );
};
